import React, { useState } from "react";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Field from "../../../components/v3/Field";
import Btn from "../../../components/v3/Btn";
import { bdcApi } from "../../../services/bdcApi";

const SECTIONS = [
  { value: "all",         label: "All sections" },
  { value: "commissions", label: "Commissions only" },
  { value: "attribution", label: "Attribution audit only" },
  { value: "sla",         label: "SLA breaches only" },
  { value: "exempt_distribution", label: "Exempt distribution (45-106F1 Sch 1)" },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function nDaysAgoISO(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function ComplianceReports() {
  const [from, setFrom] = useState(nDaysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [section, setSection] = useState("all");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function exportCsv(ev) {
    ev.preventDefault();
    setError(null);
    if (!from || !to) {
      setError("Both from and to are required.");
      return;
    }
    setBusy(true);
    try {
      const { blob, filename } = await bdcApi.adComplianceReportFetch({ from, to, section });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(href), 1000);
    } catch (err) {
      let msg = err?.message || "Export failed.";
      const body = err?.response?.data;
      if (body) {
        try {
          const text = typeof body.text === "function" ? await body.text() : null;
          if (text) {
            const parsed = JSON.parse(text);
            msg = parsed.message || msg;
          }
        } catch (_) { /* ignore */ }
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <PageShell>
      <PageHead title="Compliance reports" />

      <p style={{ color: "var(--tx-3)", fontSize: "var(--t-sm)", margin: "0 0 12px" }}>
        CSV export of commission ledger, attribution audit, SLA-breach, and exempt-distribution (45-106F1 Schedule 1) rows for a date range up to 366 days. Review before regulator submissions. Every export is itself written to <code>audit_log</code> with your user id + the selected range.
      </p>

      <section className="card">
        <div className="card__body">
          <form
            onSubmit={exportCsv}
            style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 720 }}
          >
            <div className="form-grid form-grid--3col">
              <Field
                label="From (UTC)"
                name="from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                disabled={busy}
                required
              />
              <Field
                label="To (UTC)"
                name="to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={busy}
                required
              />
              <Field
                as="select"
                label="Section"
                name="section"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                disabled={busy}
              >
                {SECTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </Field>
            </div>

            {error && <Alert tone="danger">{error}</Alert>}

            <div>
              <Btn variant="primary" type="submit" disabled={busy || !from || !to}>
                {busy ? "Exporting…" : "Download CSV"}
              </Btn>
            </div>
          </form>
        </div>
      </section>
    </PageShell>
  );
}

export default ComplianceReports;
